import * as angular from 'angular';
import * as $ from 'jquery';
import { debounce } from 'lodash';

import { tokenFromVizID } from '@mode/charts/domain';
import { TableView } from '@mode/legacy/data-access-model-js';
import { ReportDataset } from '@mode/legacy/types-app-state';
import { EmbedActionType, EmbedActions } from '@mode/reports/sandbox/contract';
import { errorGif } from '@mode/shared/ui-images';
import { isBuilderMode, isDesignerMode, isPhantomMode, isPicassoPDF, isReportMode } from '@mode/shared/util-js';
import { AnalyticsService } from '@mode/shared/util-segment';
import { clampElementWidthToAppleMobileBreakpoint, iOS } from '~/lib/ios-helpers';
import { VizTypes } from '../../common/types';
import {
  AsyncRender,
  CellSelect,
  ColumnSelect,
  ColumnSort,
  DynamicColumnWidth,
  RowHeader,
  StickyHeader,
  TextHighlight,
  VirtualScroll,
} from '../../lib/axel/plugin';
import { table as tableRenderer } from '../../lib/axel/renderer';
import { getDatasetModel, resolveColumnType } from '../../lib/dataset-helpers';
import FormatOptionsService from '../../services/format-options-service';
import ReportLinkService from '../../services/report-link-service';
import TableDataService from '../../services/table-data-service';
import { WindowMessageService } from '../../services/window-message-service';

declare function setImmediate(fn: Function);

export default class TableGridController {
  static $inject = [
    '$scope',
    '$element',
    '$window',
    'AnalyticsService',
    'ReportLinkService',
    'FormatOptionsService',
    'WindowMessageService',
  ];

  private tableID: string;
  private destroyQueue = [];
  private filterQuery: string;
  private isFilterFocus = false;
  private isClipboardEnabled = false; // handled by clipboard directive
  private capacityOverride: boolean;
  private dataset: ReportDataset;
  private view: TableView;

  private state: {
    selectedColumn: string;
    sortedColumn: {
      index: string | null;
      order: 'asc' | 'desc' | null;
    };
  };

  private onSelectColumn: ($args: { id: string; name: string }) => void;
  private onAction: ($args: { $action: EmbedActions }) => void = () => {};

  public constructor(
    private $scope,
    private $element,
    private $window,
    private as: AnalyticsService,
    private reportLinkService: ReportLinkService,
    private fos: FormatOptionsService,
    private wms: WindowMessageService
  ) {
    this.state = {
      selectedColumn: '',
      sortedColumn: {
        index: null,
        order: null,
      },
    };
  }

  $onInit() {
    this.trackReportRenderStart();
    this.adjustForMobile();

    const debouncedRender = debounce(() => this.render(), 10);
    this.$scope.$watch('selected', (isSelected) => {
      if (isSelected === true) {
        this.subscribeToWindowResize(debouncedRender);
      } else if (isSelected === false) {
        this.unsubscribeToWindowResize();
      }
    });

    if (iOS()) {
      this.subscribeToWindowOrientationChange(debouncedRender);
    }
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.view != null || changes.dataset != null) {
      // in case dataset was previously empty, wait until after
      // "No rows returned" message has been removed by Angular digest
      // cycle so that Axel can measure the height of the table.
      // Use setImmediate (shimmed by CoreJS) instead of setTimeout to
      // avoid flicker, and instead of $$postDigest() because it's
      // removed in later Angular
      // setImmediate(() => this.render());
      // Removed because setImmediate was never added to the standard so we shouldn't rely on it

      setTimeout(() => this.render());
    }
  }

  private trackReportRenderStart() {
    const tableToken = this.tableToken();

    if (this.isReportMode()) {
      this.as.startVizRenderReportTiming(tableToken);
    }
    if (this.isDesignerMode()) {
      this.as.startVizRenderDesignerTiming(tableToken);
    }
  }

  private adjustForMobile() {
    clampElementWidthToAppleMobileBreakpoint(this.$element.children().first());
  }

  private trackReportRenderEnd() {
    const tableToken = this.tableToken();

    if (this.isReportMode()) {
      this.as.endVizRenderReportTiming(tableToken, VizTypes.Table);
    }
    if (this.isDesignerMode()) {
      this.as.endVizRenderDesignerTiming(tableToken, VizTypes.Table);
    }
    this.as.endTableSortTiming(this.tableToken(), this.dataset);
  }

  public trackColumnSelectStart() {
    this.as.startColumnSelectTiming(this.tableToken());
  }

  public trackColumnSelectEnd() {
    this.as.endColumnSelectTiming(this.tableToken(), this.dataset);
  }

  private getTableId() {
    return this.tableID;
  }

  private tableToken(): string {
    return tokenFromVizID(this.getTableId());
  }

  private queryToken(): string {
    return this.dataset && this.dataset.query_token;
  }

  private queryName() {
    // TODO write a query data property getter for TDS
    const queryData = this.getQueryData();
    const queryName = queryData && queryData['queryName'];

    return queryName || '';
  }

  private showTable() {
    return this.dataset != null && this.view != null;
  }

  private reportDataURL() {
    const queryData = this.getQueryData();
    const queryName = queryData && queryData['reportDataUrl'];

    return queryName || '';
  }

  private reportVizURL() {
    return (this.view && this.view._vizReportRunLink) || '';
  }

  private copySuccess() {
    this.onAction({
      $action: {
        type: EmbedActionType.ShowAlert,
        success: true,
        message: 'Link to table copied successfully',
      },
    });
  }

  private isDesignerMode() {
    return isDesignerMode(this.$window.document.body);
  }

  private isReportMode() {
    return isReportMode(this.$window.document.body);
  }

  private isBuilderMode() {
    return isBuilderMode(this.$window.document.body);
  }

  private isPicassoPDF() {
    const result = isPicassoPDF(this.$window.document.body);
    return result;
  }

  private isPhantomMode() {
    return isPhantomMode(this.$window);
  }

  private dataAvailable() {
    return this.dataset && this.dataset.count > 0;
  }

  private selectFilterInput(evt) {
    evt.preventDefault();
    $(evt.target).parents('.table-filter-module').find('input').focus();
  }

  private clearFilterInput(evt) {
    evt.preventDefault();
    this.filterQuery = '';
    this.applyFilter('');
  }

  private dataState() {
    return this.boundDataState();
  }

  private boundDataState() {
    const state = this.dataset && this.dataset.state;

    if (!state) {
      return 'failed';
    }

    if (state === 'succeeded' || state === 'ready') {
      const count = this.dataset && this.dataset.count;
      const oversized = this.dataset && this.dataset.oversized;

      if (oversized) {
        return 'oversized';
      }

      if (count === 0) {
        return 'empty';
      }

      return 'succeeded';
    }
  }

  private rowLimit() {
    return TableDataService.DISPLAY_ROW_LIMIT;
  }

  private exceedsRowLimit() {
    const limit = this.rowLimit();
    const count = this.dataset ? this.dataset.count : 0;

    return count > limit;
  }

  private applyFilter(filterQuery) {
    this.$scope.filterQuery = filterQuery;
    this.render();
  }

  private selectAllInputText(evt) {
    this.setFilterFocus();
    evt.target.setSelectionRange(0, evt.target.value.length);
  }

  public setFilterFocus() {
    this.isFilterFocus = true;
  }

  public removeFilterFocus() {
    this.isFilterFocus = false;
  }

  private subscribeToWindowResize(callback) {
    const nsResize = this.namespacedEventLabel('resize');
    const $window = angular.element(this.$window);

    $window.off(nsResize);
    // re-render on window resize
    $window.on(nsResize, callback);
    // register cleanup
    this.$scope.$on('$destroy', () => $window.off(nsResize, callback));
  }

  private unsubscribeToWindowResize() {
    angular.element(this.$window).off(this.namespacedEventLabel('resize'));
  }

  private subscribeToWindowOrientationChange(callback) {
    if (iOS()) {
      // re-render on orientation change
      this.$window.addEventListener('orientationchange', callback, false);
      // register cleanup
      this.$scope.$on('$destroy', () => this.$window.removeEventListener('orientationchange', callback, false));
    }
  }

  private namespacedEventLabel(label) {
    return [label, this.tableToken()].join('.');
  }

  private getTableContainer(element: angular.IAugmentedJQuery) {
    return element[0].querySelector('.js-table-container');
  }

  private getQueryData() {
    return this.dataset;
  }

  private getTableData(opts = {}) {
    return this.dataset && getDatasetModel(this.dataset, opts);
  }

  private linkTo($event, type) {
    if (this.reportLinkService.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(
        type === 'lightbox' ? { id: this.tableID, view: this.view } : this.queryToken(),
        type
      );
    }
  }

  private isLinkable() {
    return this.dataAvailable() && this.reportLinkService.isLinkable();
  }

  private errorImage() {
    return errorGif;
  }

  /**
   * Renderer
   * @param containerEl
   * @returns {function(): undefined}
   */
  private render() {
    const containerEl = this.getTableContainer(this.$element);
    if (!containerEl) {
      throw new Error('Table container not provided');
    }

    if (this.dataset == null || this.view == null) {
      return;
    }

    this.adjustForMobile();

    if (this.dataAvailable()) {
      this.destroyQueueFlush();
      const renderer = this.getTableRenderer();
      const sort = this.state.sortedColumn;
      const minQueryLength = TableDataService.MIN_FILTER_QUERY_LENGTH;
      const filterQuery = this.$scope.filterQuery;
      const formatters = this.fos.getPresetFormatters(this.view.columns);
      const excludedColumns = this.view.excludeColumns || [];
      const columnIsNotExcluded = (colKey) => !~excludedColumns.indexOf(colKey);
      const isVerticallyScrollable = !this.componentDimensionsAtCapacity(this.$element);
      const data = this.getTableData({
        formatter: (data, colKey) => () => this.applyFormatter.call(this, data, colKey, formatters),
        filterColumns: (cols) => cols.filter(columnIsNotExcluded),
        memoize: true,
      });
      const virtualScrollOptions = {
        rowHeight: 26,
        dimensionsAtCapacity: this.componentDimensionsAtCapacity.bind(this),
        selectedCol: this.getSelectedColumn.bind(this),
      };
      const columnSortOptions = {
        sortCol: this.sortColumn.bind(this),
        sortedCol: this.getSortedColumn.bind(this),
        onDropdownOpen: this.selectColumn.bind(this, data),
      };
      const columnSelectOptions = {
        selectCol: this.selectColumn.bind(this, data),
        selectedCol: this.getSelectedColumn.bind(this),
      };
      const cellSelectOptions = {};
      const dynamicColumnWidthOptions = {
        onColumnWidthUpdate: this.centerSelectedColumn.bind(this),
      };
      const asyncRenderOptions = {
        dimensionsAtCapacity: this.componentDimensionsAtCapacity.bind(this),
      };
      const textHighlightOptions = {
        pattern: filterQuery,
        minQueryLength: minQueryLength,
      };

      // prepare data for rendering
      if (filterQuery && filterQuery.length >= minQueryLength) {
        data.filterRows(filterQuery);
      }
      if (sort && parseInt(sort.index) >= 0 && sort.order) {
        data.sort(parseInt(sort.index) - 1, sort.order);
      }

      // initialize renderer
      const render = this.initRenderer(renderer, data, containerEl);
      // enable/apply plugins
      if (!this.isPicassoPDF()) {
        render.plugin(VirtualScroll, virtualScrollOptions);
      }
      render.plugin(RowHeader, {});
      render.plugin(ColumnSort, columnSortOptions);
      render.plugin(ColumnSelect, columnSelectOptions);
      render.plugin(CellSelect, cellSelectOptions);
      if (isVerticallyScrollable) {
        render.plugin(StickyHeader, {});
      }
      render.plugin(DynamicColumnWidth, dynamicColumnWidthOptions);
      render.plugin(AsyncRender, asyncRenderOptions);
      render.plugin(TextHighlight, textHighlightOptions);
      render();
      this.destroyQueueAdd(render.destroy);
    }
    this.trackReportRenderEnd();
    // Still trigger TableRendered for when table has no rows
    this.onAction({
      $action: {
        type: EmbedActionType.TableRendered,
        token: this.tableToken(),
      },
    });
  }

  private centerSelectedColumn({ $table, containerEl }) {
    const $container = $(containerEl);
    const $selectedCol = $table.find('.is-selected-col');
    const containerWidth = $container.outerWidth(true);
    let selectedColWidth;

    if ($selectedCol.length) {
      selectedColWidth = $selectedCol.outerWidth(true);
      $container.scrollLeft($selectedCol.prop('offsetLeft') - containerWidth / 2 + selectedColWidth / 2);
    }
  }

  private destroyQueueAdd(fn) {
    this.destroyQueue.push(fn);
  }

  private destroyQueueFlush() {
    this.destroyQueue.forEach((fn) => fn());
    this.destroyQueue = [];
  }

  /**
   * Apply Formatter
   *
   * @todo must cache these values.. this is an expensive call
   * @param data
   * @param colKey
   * @param currencyFormatters
   * @returns {any}
   */
  private applyFormatter(data, colKey: string, currencyFormatters) {
    const colType = resolveColumnType(this.dataset, this.view.columns, colKey);
    const formatter = this.fos.getFormatter(this.view.columns, colKey, data, colType, currencyFormatters);

    return formatter(data);
  }

  /**
   * Sort Column
   *
   * @param index
   * @param order
   */
  private sortColumn(index, order: 'asc' | 'desc') {
    const { index: prevIndex, order: prevOrder } = this.getSortedColumn();

    this.state.sortedColumn = { index, order };
    if (!(prevIndex === index && prevOrder === order)) {
      this.as.startTableSortTiming(this.tableToken());
      this.render();
    }
  }

  /**
   * Get Sorted Column
   *
   * @returns {index, order}
   */
  private getSortedColumn() {
    return this.state.sortedColumn;
  }

  /**
   * Select Column
   *
   * Broadcast column selection to the parent/application and update
   * the view options service state
   * @todo investigate if this fires multiple watcher events
   * @param data
   * @param index
   */
  private selectColumn(data, index) {
    const colIndex = index - 1;
    const colKey = data.cols()[colIndex];

    if (this.isBuilderMode()) {
      this.postColumnSelection(this.getTableId(), colKey);
    } else if (this.isDesignerMode()) {
      this.applyColumnSelection(this.getTableId(), colKey);
    }

    this.state.selectedColumn = index;
    this.trackColumnSelectEnd();
  }

  /**
   * Post Column Selection
   *
   * Pushes column selection to window message service and parent application
   * @param tableID
   * @param colKey
   */
  private postColumnSelection(tableID, colKey) {
    this.wms.selectTableColumn(tableID, colKey);
  }

  /**
   * Apply Column Selection
   *
   * Pushes column selection to scope/parent-component
   * @param tableID
   * @param colKey
   */
  private applyColumnSelection = (tableID, colKey) => {
    this.$scope.$evalAsync(() => {
      this.onSelectColumn({
        id: tableID,
        name: colKey,
      });
    });
  };

  /**
   * Get Selected Column
   *
   * @returns {any|null}
   */
  private getSelectedColumn() {
    return this.state.selectedColumn;
  }

  /**
   * Get Table Renderer
   *
   * @returns {function(any, HTMLElement, rendererOptions=): any}
   */
  private getTableRenderer() {
    return tableRenderer;
  }

  /**
   * Component Dimensions Restricted
   *
   * Hackey way to determine if this component has been downsized from
   * the default full width state in the report view
   * @param containerEl
   * @returns {boolean}
   */
  private componentDimensionsAtCapacity(tableEl) {
    if (this.capacityOverride) {
      return true;
    }

    const $table = $(tableEl);
    const childOf = ($el, selector) => $el.closest(selector).length > 0;
    const fullWidthReportComponentSelector = '.mode-embed .row:not([data-row-height]) .col-md-12';
    return childOf($table, fullWidthReportComponentSelector);
  }

  private initRenderer(renderer: any, data: any, containerEl: any) {
    return renderer(data, containerEl, this.getRendererOptions());
  }

  private getRendererOptions() {}
}
